// components/designer/DesignerHeader.tsx
/**
 * Designer Header Component
 *
 * Top toolbar containing:
 * - Template name input
 * - Page size selector
 * - Double-sided toggle
 * - Front/back switcher
 * - Tool selection (select/move)
 * - Grid controls
 * - Canvas/Preview toggle
 * - Export menu
 * - Save button
 */

import { Button } from "@shared/ui/button";
import { InfoTooltip } from "@shared/ui/infoTooltip";
import { Input } from "@shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  Download,
  Eye,
  Grid3X3,
  Layout,
  MousePointer,
  Move,
  Save,
} from "lucide-react";
import React, { useState } from "react";
import { useTemplateChoices } from "../../hooks";
import { DesignerGuide } from "../designer/DesignerGuide";

const PAGE_SIZES = {
  a4: { width: 794, height: 1123, label: "A4 (210×297mm)" },
  letter: { width: 816, height: 1056, label: 'Letter (8.5×11")' },
  legal: { width: 816, height: 1344, label: 'Legal (8.5×14")' },
  // custom: { width: 800, height: 1000, label: "Custom" },
};

interface DesignerHeaderProps {
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  pageSize: string;
  onPageSizeChange: (size: string) => void;
  isDoubleSided: boolean;
  onDoubleSidedChange: (value: boolean) => void;
  activeSide: "front" | "back";
  onSideChange: (side: "front" | "back") => void;
  tool: "select" | "move";
  onToolChange: (tool: "select" | "move") => void;
  showGrid: boolean;
  onShowGridChange: (value: boolean) => void;
  snapToGrid: boolean;
  onSnapToGridChange: (value: boolean) => void;
  activeTab: "canvas" | "preview";
  onTabChange: (tab: "canvas" | "preview") => void;
  onExport: (format: "pdf" | "jpg" | "png") => void;
  onSave: () => void;
  isSaving: boolean;
  isExporting: boolean;
  templateType: string;
  onTemplateTypeChange: (type: string) => void;
  category: string;
  onCategoryChange: (category: string) => void;
  perforationEnabled: boolean;
  onPerforationEnabledChange: (enabled: boolean) => void;
  perforationY: number;
  onPerforationYChange: (y: number) => void;
}

const DesignerHeader: React.FC<DesignerHeaderProps> = ({
  templateName,
  onTemplateNameChange,
  pageSize,
  onPageSizeChange,
  isDoubleSided,
  onDoubleSidedChange,
  activeSide,
  onSideChange,
  tool,
  onToolChange,
  showGrid,
  onShowGridChange,
  snapToGrid,
  onSnapToGridChange,
  activeTab,
  onTabChange,
  onExport,
  onSave,
  isSaving,
  isExporting,
  templateType,
  onTemplateTypeChange,
  category,
  onCategoryChange,
  perforationEnabled,
  onPerforationEnabledChange,
  perforationY,
  onPerforationYChange,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const { data: templateTypes } = useTemplateChoices("template_type");
  const { data: categories } = useTemplateChoices("template");
  const handleExport = (format: "pdf" | "jpg" | "png") => {
    setShowExportMenu(false);
    onExport(format);
  };

  return (
    <div className="bg-white border-b shadow-sm">
      {/* Top Row: General Settings */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-6 flex-1">
          {/* Template Name */}
          <div className="flex flex-col gap-1">
            <div className="flex-item">
              <label className="text-sm mr-2">Template Name</label>
              <InfoTooltip
                content={"The unique name for this bill template."}
              />
            </div>
            <Input
              value={templateName}
              onChange={(e) => onTemplateNameChange(e.target.value)}
              className="h-9 px-3 w-84 border-gray-200 focus:border-blue-500 transition-colors"
              placeholder="Enter name..."
            />
          </div>

          {/* Template Type */}
          <div className="flex flex-col gap-1">
            <div className="flex-item">
              <label className="text-sm mr-2">Template Type</label>
              <InfoTooltip
                content={
                  "The type of bill (e.g., Utility, Invoice) which determines the initial layout."
                }
              />
            </div>
            <Select value={templateType} onValueChange={onTemplateTypeChange}>
              <SelectTrigger className="h-9 w-48 border-gray-200">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {templateTypes?.templateType?.map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    {type.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <div className="flex-item">
              <label className="text-sm mr-2">Category</label>
              <InfoTooltip
                content={
                  "A category to organize your templates (e.g., Residential, Commercial)."
                }
              />
            </div>
            <Select value={category} onValueChange={onCategoryChange}>
              <SelectTrigger className="h-9 w-64 border-gray-200">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories?.templateCategories?.map((cat) => (
                  <SelectItem key={cat.key} value={cat.key}>
                    {cat.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Double-sided toggle wrapper */}
          <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-full border border-gray-200">
            <div className="flex-item">
              <span className="text-sm font-semibold text-gray-700 mr-2">
                Double-sided
              </span>
              <InfoTooltip
                content={
                  "Enable if this template should have a back side for printing."
                }
              />
            </div>
            <div
              className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-200 ${isDoubleSided ? "bg-blue-600" : "bg-gray-300"
                }`}
              onClick={() => onDoubleSidedChange(!isDoubleSided)}
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${isDoubleSided ? "translate-x-5" : "translate-x-0"
                  }`}
              />
            </div>
          </div>

          {/* Save button */}
          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            disabled={isSaving}
          >
            <Save size={18} className="mr-2" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Bottom Row: Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50/50">
        <div className="flex items-center gap-4">
          {/* Front/Back switcher */}
          {isDoubleSided && (
            <div className="flex p-0.5 bg-gray-200/70 rounded-lg">
              <Button
                variant={activeSide === "front" ? "default" : "ghost"}
                size="sm"
                onClick={() => onSideChange("front")}
                className={`h-7 px-4 text-xs font-bold rounded-md transition-all ${activeSide === "front"
                  ? "bg-white text-gray-900 shadow-sm border border-gray-100"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Front
              </Button>
              <Button
                variant={activeSide === "back" ? "default" : "ghost"}
                size="sm"
                onClick={() => onSideChange("back")}
                className={`h-7 px-4 text-xs font-bold rounded-md transition-all ${activeSide === "back"
                  ? "bg-white text-gray-900 shadow-sm border border-gray-100"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Back
              </Button>
            </div>
          )}

          {/* Perforation Line controls */}
          {isDoubleSided && (
            <div className="flex items-center gap-2 border-l border-gray-200 pl-1">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={perforationEnabled}
                  onChange={(e) => onPerforationEnabledChange(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-xs font-bold text-gray-700 group-hover:text-blue-600 transition-colors">
                  Perforation Line
                </span>
              </label>
              {perforationEnabled && (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 transition-all">
                  <span className="text-[10px] text-gray-400 uppercase font-black tracking-widest">
                    Height:
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      value={perforationY}
                      onChange={(e) =>
                        onPerforationYChange(parseInt(e.target.value) || 0)
                      }
                      className="h-7 w-16 text-xs px-2 border-gray-200 focus:ring-1 focus:ring-blue-400"
                    />
                    <span className="text-[10px] text-gray-400 font-bold">
                      px
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Page Size */}
          <div className="border-l border-gray-200 pl-2">
            <Select value={pageSize} onValueChange={onPageSizeChange}>
              <SelectTrigger className="h-7 w-44 text-xs px-3 bg-white border-gray-200 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PAGE_SIZES).map(([key, size]) => (
                  <SelectItem key={key} value={key}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Tool selection */}
          <div className="flex p-0.5 bg-gray-200/70 rounded-lg mr-1">
            <Button
              variant={tool === "select" ? "default" : "ghost"}
              size="sm"
              onClick={() => onToolChange("select")}
              className={`h-7 px-3 gap-2 rounded-md transition-all ${tool === "select"
                ? "bg-white text-blue-600 shadow-sm border border-gray-100"
                : "text-gray-500"
                }`}
            >
              <MousePointer size={14} />
              <span className="text-[10px] font-black uppercase tracking-widest">
                Select
              </span>
            </Button>
            <Button
              variant={tool === "move" ? "default" : "ghost"}
              size="sm"
              onClick={() => onToolChange("move")}
              className={`h-7 px-3 gap-2 rounded-md transition-all ${tool === "move"
                ? "bg-white text-blue-600 shadow-sm border border-gray-100"
                : "text-gray-500"
                }`}
            >
              <Move size={14} />
              <span className="text-[10px] font-black uppercase tracking-widest">
                Move
              </span>
            </Button>
          </div>

          {/* Icons Group */}
          <div className="flex items-center gap-1.5 border-l border-gray-200 pl-3">
            <Button
              variant={showGrid ? "default" : "ghost"}
              size="sm"
              onClick={() => onShowGridChange(!showGrid)}
              className={`h-7 w-7 p-0 rounded-md transition-colors ${showGrid
                ? "bg-blue-100 text-blue-600"
                : "text-gray-500 hover:bg-gray-200"
                }`}
            >
              <Grid3X3 size={16} />
            </Button>

            <div className="flex rounded-lg bg-gray-200/70 p-0.5 ml-2">
              <Button
                variant={activeTab === "canvas" ? "default" : "ghost"}
                size="sm"
                onClick={() => onTabChange("canvas")}
                className={`h-7 px-4 text-xs font-bold rounded-md transition-all ${activeTab === "canvas"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500"
                  }`}
              >
                <Layout size={14} className="mr-2" />
                Canvas
              </Button>
              <Button
                variant={activeTab === "preview" ? "default" : "ghost"}
                size="sm"
                onClick={() => onTabChange("preview")}
                className={`h-7 px-4 text-xs font-bold rounded-md transition-all ${activeTab === "preview"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500"
                  }`}
              >
                <Eye size={14} className="mr-2" />
                Preview
              </Button>
            </div>
          </div>

          {/* Export button */}
          <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting}
                className="h-8 border-gray-300 text-gray-700 font-bold px-4 hover:bg-gray-100 active:scale-95 transition-all"
              >
                <Download size={14} className="mr-2" />
                {isExporting ? "Exporting..." : "Export"}
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full px-4 py-3 text-sm font-medium text-left hover:bg-gray-50 flex items-center justify-between transition-colors text-gray-700"
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      PDF Document
                    </span>
                    <span className="text-[10px] text-blue-600 bg-blue-50 font-black border border-blue-100 rounded px-1.5 py-0.5">
                      PDF
                    </span>
                  </button>
                  <div className="h-px bg-gray-100 mx-2" />
                  <button
                    onClick={() => handleExport("jpg")}
                    className="w-full px-4 py-3 text-sm font-medium text-left hover:bg-gray-50 flex items-center justify-between transition-colors text-gray-700"
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      High Quality Image
                    </span>
                    <span className="text-[10px] text-orange-600 bg-orange-50 font-black border border-orange-100 rounded px-1.5 py-0.5">
                      JPG
                    </span>
                  </button>
                  <button
                    onClick={() => handleExport("png")}
                    className="w-full px-4 py-3 text-sm font-medium text-left hover:bg-gray-50 flex items-center justify-between transition-colors text-gray-700"
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      Transparent Image
                    </span>
                    <span className="text-[10px] text-green-600 bg-green-50 font-black border border-green-100 rounded px-1.5 py-0.5">
                      PNG
                    </span>
                  </button>
                </div>
              )}
            </div>

            <DesignerGuide />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignerHeader;
